// Recipe photos (035_recipe_photos.sql): resized on the device before upload,
// so a 12-megapixel camera shot leaves the phone as a JPEG of a few hundred kB.
// iOS hands a picked HEIC over as JPEG already; anything the browser can't
// decode is refused here rather than uploaded as-is.

/** The long edge a photo is scaled down to. A card is 128 px tall; a desktop detail dialog shows more. */
export const PHOTO_MAX_EDGE = 1600
/**
 * An imported photo (a recipe page's image or a video's thumbnail) is kept
 * smaller: enough to recognise the dish on a card and the recipe page, not a
 * full-size copy of someone else's photo.
 */
export const IMPORTED_PHOTO_EDGE = 640
export const PHOTO_QUALITY = 0.82
/** The bucket's own limit (035) — after resizing a photo is far below it. */
export const PHOTO_MAX_BYTES = 2 * 1024 * 1024
export const PHOTO_BUCKET = 'recipe-photos'

/** Scales (w, h) to fit within `max` on the long edge, never up. */
export function fitWithin(w: number, h: number, max: number = PHOTO_MAX_EDGE): { width: number; height: number } {
  if (w <= 0 || h <= 0) return { width: 0, height: 0 }
  const scale = Math.min(1, max / Math.max(w, h))
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) }
}

/**
 * Where a recipe's photo goes: its household's folder (the storage policies
 * key on it) and a fresh name per upload, so a replaced photo is never served
 * from a cache under the old one's URL.
 */
export function photoPath(householdId: string, recipeId: string, random: string = crypto.randomUUID().slice(0, 8)): string {
  return `${householdId}/${recipeId}-${random}.jpg`
}

export type PhotoError = 'not_an_image' | 'too_large'

/** A picked file as a resized JPEG, or why not. */
export async function resizePhoto(file: Blob, maxEdge: number = PHOTO_MAX_EDGE): Promise<{ ok: true; blob: Blob } | { ok: false; error: PhotoError }> {
  if (!file.type.startsWith('image/')) return { ok: false, error: 'not_an_image' }
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return { ok: false, error: 'not_an_image' }
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height, maxEdge)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return { ok: false, error: 'not_an_image' }
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY))
  if (!blob) return { ok: false, error: 'not_an_image' }
  if (blob.size > PHOTO_MAX_BYTES) return { ok: false, error: 'too_large' }
  return { ok: true, blob }
}
